/**
 * Turn whatever the backend sends into a flat { flag: boolean } map.
 */

import {
  NESTED_ACTION_ALIASES,
  PERMISSION_ALIASES,
  PERMISSION_DEPENDENTS,
  PERMISSION_FLAGS,
} from './catalog';

const KNOWN_FLAGS = new Set(PERMISSION_FLAGS);

const PERMISSION_BAG_KEYS = [
  'permissions',
  'teamLeaderPermissions',
  'permissionFlags',
  'caps',
  'capabilities',
];

export function isGranted(value) {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on';
  }
  return false;
}

export function normalizeAliasKey(rawKey) {
  if (!rawKey && rawKey !== 0) return '';
  return String(rawKey)
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase();
}

export function resolvePermissionFlag(rawKey) {
  const aliasKey = normalizeAliasKey(rawKey);
  if (!aliasKey) return null;
  if (KNOWN_FLAGS.has(aliasKey)) return aliasKey;
  if (PERMISSION_ALIASES[aliasKey]) return PERMISSION_ALIASES[aliasKey];
  const compact = aliasKey.replace(/_/g, '');
  if (PERMISSION_ALIASES[compact]) return PERMISSION_ALIASES[compact];
  return null;
}

function writeFlag(out, rawKey, granted) {
  const flag = resolvePermissionFlag(rawKey);
  if (!flag) return;
  if (granted) {
    out[flag] = true;
    return;
  }
  if (out[flag] !== true) {
    out[flag] = false;
  }
}

function flattenInto(source, out) {
  if (!source) return out;

  if (typeof source === 'string') {
    writeFlag(out, source, true);
    return out;
  }

  if (Array.isArray(source)) {
    source.forEach((item) => flattenInto(item, out));
    return out;
  }

  if (typeof source !== 'object') return out;

  Object.entries(source).forEach(([key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      Object.entries(NESTED_ACTION_ALIASES).forEach(([action, suffix]) => {
        if (isGranted(value[action])) {
          writeFlag(out, suffix ? `${key}${suffix}` : key, true);
        }
      });
      flattenInto(value, out);
      return;
    }

    if (Array.isArray(value) && value.every((item) => typeof item === 'string' || typeof item === 'object')) {
      flattenInto(value, out);
      return;
    }

    writeFlag(out, key, isGranted(value));
  });

  return out;
}

function looksLikePermissionMap(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  if (keys.length === 0) return false;
  return keys.some((key) => {
    const flag = resolvePermissionFlag(key);
    return KNOWN_FLAGS.has(flag);
  });
}

/**
 * @param {unknown} source
 * @returns {Record<string, boolean>}
 */
export function normalizePermissions(source) {
  return flattenInto(source, {});
}

/**
 * Pull permission bags off login / team-leader / me payloads.
 * @param {unknown} payload
 * @returns {Record<string, boolean>}
 */
export function extractPermissionsFromPayload(payload) {
  if (!payload) return {};
  const root = payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)
    ? payload.data
    : payload;
  const bags = [];

  const collectBags = (obj) => {
    if (!obj || typeof obj !== 'object') return;
    PERMISSION_BAG_KEYS.forEach((key) => {
      if (obj[key]) bags.push(obj[key]);
    });
  };

  collectBags(root);
  collectBags(root.user);
  collectBags(root.employee);
  collectBags(root.teamLeader);

  // A dedicated permission object may arrive unwrapped.
  if (!bags.length && looksLikePermissionMap(root) && !root.role && !root.userId && !root.email) {
    bags.push(root);
  }

  const merged = {};
  bags.forEach((bag) => Object.assign(merged, normalizePermissions(bag)));
  return merged;
}

function emptyPermissionMap() {
  return PERMISSION_FLAGS.reduce((acc, flag) => {
    acc[flag] = false;
    return acc;
  }, {});
}

/**
 * Catalog flags only. Missing keys stay false unless `defaults` fills them.
 * Pass TEAM_LEADER_PERMISSION_DEFAULTS only for a new assign form — never
 * when loading a saved map, or omitted flags look granted.
 */
export function sanitizeDependentPermissions(map = {}) {
  const next = { ...map };
  Object.entries(PERMISSION_DEPENDENTS).forEach(([parent, deps]) => {
    if (isGranted(next[parent])) return;
    deps.forEach((dep) => {
      next[dep] = false;
    });
  });
  return next;
}

export function pickKnownPermissions(source, defaults = emptyPermissionMap()) {
  const normalized = normalizePermissions(source);
  const next = { ...emptyPermissionMap(), ...defaults };
  PERMISSION_FLAGS.forEach((flag) => {
    if (normalized[flag] === true) next[flag] = true;
    else if (normalized[flag] === false) next[flag] = false;
  });
  return sanitizeDependentPermissions(next);
}

export function mergePermissionMaps(...maps) {
  const merged = {};
  maps.forEach((map) => {
    if (!map) return;
    Object.entries(normalizePermissions(map)).forEach(([key, value]) => {
      if (value === true || value === false) merged[key] = value;
    });
  });
  return merged;
}
